/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  FileText,
  Image as ImageIcon,
  ExternalLink,
  ShieldCheck,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import "./EvidencePreviewModal.css";

export default function EvidencePreviewModal({
  open,
  onClose,
  url,
  fileName,
  loading,
  error,
}) {
  const safeName = fileName || "Supporting document";
  const cleanUrl = String(url || "").split("?")[0];
  const isImage = Boolean(url) && /\.(png|jpg|jpeg|webp|gif)$/i.test(cleanUrl);
  const [assetReady, setAssetReady] = useState(false);
  const [closing, setClosing] = useState(false);
  const closeQueuedRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setClosing(false);
    setAssetReady(false);
    closeQueuedRef.current = false;
  }, [open, url]);

  const requestClose = () => {
    if (closeQueuedRef.current) return;
    closeQueuedRef.current = true;

    // Hide the entire secure surface before the browser destroys a PDF iframe.
    // This prevents the white/black repaint flash seen during iframe teardown.
    setClosing(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => onClose?.());
    });
  };

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") requestClose();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  const showLoading = loading || (Boolean(url) && !assetReady);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className={`evidenceOverlay${closing ? " isClosing" : ""}`}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <section
        className="evidenceModal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="evidence-preview-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="evidenceHeader">
          <div className="evidenceTitleWrap">
            <div className="evidenceIcon" aria-hidden="true">
              {isImage ? <ImageIcon size={20} /> : <FileText size={20} />}
            </div>

            <div>
              <h2 id="evidence-preview-title">Supporting Evidence</h2>
              <p>{safeName}</p>
            </div>
          </div>

          <button
            type="button"
            className="evidenceCloseBtn"
            onClick={requestClose}
            aria-label="Close evidence preview"
          >
            <X size={20} />
          </button>
        </div>

        <div className="evidenceSecurityBar">
          <ShieldCheck size={16} aria-hidden="true" />
          Secure temporary document preview
        </div>

        <div className="evidenceBody">
          {url && isImage ? (
            <img
              src={url}
              alt={safeName}
              className={`evidenceImagePreview${assetReady ? " isReady" : ""}`}
              onLoad={() => setAssetReady(true)}
              onError={() => setAssetReady(true)}
            />
          ) : url ? (
            <iframe
              src={url}
              title={safeName}
              className={`evidenceFrame${assetReady ? " isReady" : ""}`}
              loading="eager"
              onLoad={() => setAssetReady(true)}
            />
          ) : null}

          {showLoading && !error ? (
            <div className="evidenceState evidenceStateOverlay">
              <Loader2 className="spin" size={30} aria-hidden="true" />
              <h3>{url ? "Opening document..." : "Fetching document..."}</h3>
              <p>Please wait while we prepare the secure preview.</p>
            </div>
          ) : null}

          {error ? (
            <div className="evidenceState error evidenceStateOverlay">
              <AlertTriangle size={32} aria-hidden="true" />
              <h3>Could not load evidence</h3>
              <p>{error}</p>
            </div>
          ) : null}
        </div>

        <div className="evidenceFooter">
          <span>This secure link may expire automatically.</span>

          {url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="evidenceOpenExternal"
            >
              Open in new tab
              <ExternalLink size={15} aria-hidden="true" />
            </a>
          ) : null}
        </div>
      </section>
    </div>,
    document.body
  );
}
